import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from './auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from './components/page-elements';

const initialReceiverForm = {
  receiverName: '',
  billingAddress1: '',
  billingAddress2: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
};

const validateReceiver = (form) => {
  const errors = {};
  if (!form.receiverName?.trim()) errors.receiverName = 'Receiver name is required.';
  if (!form.billingAddress1?.trim()) errors.billingAddress1 = 'Billing address is required.';
  if (!form.billingCity?.trim()) errors.billingCity = 'Billing city is required.';
  if (!form.billingState?.trim()) errors.billingState = 'Billing state is required.';
  if (!form.billingZip?.trim()) errors.billingZip = 'Billing zip is required.';
  return errors;
};

const getCustomerId = (row) => String(row?.customerName?._id || row?.customerName || '');

const Receivers = ({ forcedCustomerId = '', embedded = false }) => {
  const navigate = useNavigate();
  const [receivers, setReceivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedReceiver, setSelectedReceiver] = useState(null);
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [editFieldErrors, setEditFieldErrors] = useState({});

  const [createForm, setCreateForm] = useState(initialReceiverForm);
  const [editForm, setEditForm] = useState(initialReceiverForm);

  const user = useMemo(() => getStoredUser(), []);
  const canManage = user?.role === 'admin' || user?.role === 'internal' || user?.role === 'customer';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchReceivers = useCallback(async () => {
    try {
      const query = forcedCustomerId ? `?customerId=${forcedCustomerId}` : '';
      const response = await fetch(`${API_BASE_URL}/api/receivers${query}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();
      const rows = Array.isArray(data) ? data : [];
      const scopedRows = forcedCustomerId
        ? rows.filter((row) => getCustomerId(row) === String(forcedCustomerId))
        : rows;
      setReceivers(scopedRows);
      setLoading(false);
    } catch (err) {
      setLoadError(err.message);
      setLoading(false);
    }
  }, [forcedCustomerId]);

  useEffect(() => {
    fetchReceivers();
  }, [fetchReceivers]);

  const openCreate = () => setCreateOpen(true);

  const closeCreate = () => {
    setCreateOpen(false);
    setActionError('');
    setCreateFieldErrors({});
    setCreateForm(initialReceiverForm);
  };

  const openEdit = (receiver) => {
    setSelectedReceiver(receiver);
    setEditFieldErrors({});
    setEditForm({
      receiverName: receiver.receiverName || '',
      billingAddress1: receiver.billingAddress1 || '',
      billingAddress2: receiver.billingAddress2 || '',
      billingCity: receiver.billingCity || '',
      billingState: receiver.billingState || '',
      billingZip: receiver.billingZip || '',
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setSelectedReceiver(null);
    setActionError('');
    setEditFieldErrors({});
  };

  const saveCreate = async () => {
    const errors = validateReceiver(createForm);
    setCreateFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setActionError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/receivers`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...createForm,
          ...(forcedCustomerId ? { customerName: forcedCustomerId } : {}),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to create receiver');
      }

      const data = await response.json();
      setReceivers((prev) => [data.receiver, ...prev]);
      closeCreate();
    } catch (err) {
      setActionError(err.message);
    }

    setSaving(false);
  };

  const saveEdit = async () => {
    if (!selectedReceiver?._id) return;

    const errors = validateReceiver(editForm);
    setEditFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setActionError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/receivers/${selectedReceiver._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify(editForm),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to update receiver');
      }

      const data = await response.json();
      setReceivers((prev) => prev.map((r) => (r._id === data.receiver._id ? data.receiver : r)));
      closeEdit();
    } catch (err) {
      setActionError(err.message);
    }

    setSaving(false);
  };

  const deactivateReceiver = async (receiverId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/receivers/${receiverId}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ isActive: false }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to deactivate receiver');
      }

      const data = await response.json();
      setReceivers((prev) => prev.map((r) => (r._id === data.receiver._id ? data.receiver : r)));
    } catch (err) {
      setActionError(err.message);
    }
  };

  if (loading) return <LoadingPane label="Loading receivers..." />;

  return (
    <Box p={embedded ? 0 : 2}>
      {!embedded ? (
        <PageHeader
          title="Receivers"
          subtitle="Maintain receiver profiles and manage linked locations."
          actions={canManage ? [<Button key="add" variant="contained" onClick={openCreate}>Add Receiver</Button>] : null}
        />
      ) : (
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Receivers</Typography>
          {canManage ? <Button variant="contained" onClick={openCreate}>Add Receiver</Button> : null}
        </Box>
      )}
      <ErrorBanner message={loadError || actionError} />

      {receivers.length === 0 ? (
        <EmptyPane title="No receivers found" />
      ) : isMobile ? (
        <Stack spacing={1.2}>
          {receivers.map((receiver) => (
            <Card key={receiver._id} variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.8}>
                  <Typography fontWeight={700}>{receiver.receiverName}</Typography>
                  <Chip size="small" label={receiver.isActive === false ? 'Inactive' : 'Active'} color={receiver.isActive === false ? 'default' : 'success'} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{receiver.fullBillingAddress || 'N/A'}</Typography>
                {canManage && (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Manage Locations"><IconButton color="secondary" onClick={() => navigate(`/projects?receiverId=${receiver._id}`)}><LocationOnIcon /></IconButton></Tooltip>
                    <Tooltip title="Add Location"><IconButton color="secondary" onClick={() => navigate(`/projects?receiverId=${receiver._id}&create=1`)}><LocationOnIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Edit receiver"><IconButton color="primary" onClick={() => openEdit(receiver)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Deactivate receiver"><span><IconButton color="warning" onClick={() => deactivateReceiver(receiver._id)} disabled={receiver.isActive === false}><BlockIcon /></IconButton></span></Tooltip>
                  </Stack>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
      <TableContainer component={Paper} elevation={0} className="surface-hover" sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#ffffff' }}>
              <TableCell>Receiver</TableCell>
              <TableCell>Main Billing Address</TableCell>
              <TableCell>Status</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {receivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 4 : 3}>No receivers found.</TableCell>
              </TableRow>
            ) : (
              receivers.map((receiver) => (
                <TableRow key={receiver._id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{receiver.receiverName}</Typography>
                  </TableCell>
                  <TableCell>{receiver.fullBillingAddress || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={receiver.isActive === false ? 'Inactive' : 'Active'}
                      color={receiver.isActive === false ? 'default' : 'success'}
                    />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title="Manage Locations">
                        <IconButton color="secondary" onClick={() => navigate(`/projects?receiverId=${receiver._id}`)}>
                          <LocationOnIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Add Location">
                        <IconButton color="secondary" onClick={() => navigate(`/projects?receiverId=${receiver._id}&create=1`)}>
                          <LocationOnIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit receiver">
                        <IconButton color="primary" onClick={() => openEdit(receiver)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deactivate receiver">
                        <span>
                          <IconButton color="warning" onClick={() => deactivateReceiver(receiver._id)} disabled={receiver.isActive === false}>
                            <BlockIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
        <DialogTitle>Add Receiver</DialogTitle>
        <DialogContent>
          <TextField margin="normal" name="receiverName" label="Receiver Name" value={createForm.receiverName} onChange={(e) => { setCreateForm((p) => ({ ...p, receiverName: e.target.value })); setCreateFieldErrors((p) => ({ ...p, receiverName: '' })); }} fullWidth error={Boolean(createFieldErrors.receiverName)} helperText={createFieldErrors.receiverName} />
          <TextField margin="normal" name="billingAddress1" label="Billing Address 1" value={createForm.billingAddress1} onChange={(e) => { setCreateForm((p) => ({ ...p, billingAddress1: e.target.value })); setCreateFieldErrors((p) => ({ ...p, billingAddress1: '' })); }} fullWidth error={Boolean(createFieldErrors.billingAddress1)} helperText={createFieldErrors.billingAddress1} />
          <TextField margin="normal" name="billingAddress2" label="Billing Address 2" value={createForm.billingAddress2} onChange={(e) => setCreateForm((p) => ({ ...p, billingAddress2: e.target.value }))} fullWidth />
          <TextField margin="normal" name="billingCity" label="Billing City" value={createForm.billingCity} onChange={(e) => { setCreateForm((p) => ({ ...p, billingCity: e.target.value })); setCreateFieldErrors((p) => ({ ...p, billingCity: '' })); }} fullWidth error={Boolean(createFieldErrors.billingCity)} helperText={createFieldErrors.billingCity} />
          <TextField margin="normal" name="billingState" label="Billing State" value={createForm.billingState} onChange={(e) => { setCreateForm((p) => ({ ...p, billingState: e.target.value })); setCreateFieldErrors((p) => ({ ...p, billingState: '' })); }} fullWidth error={Boolean(createFieldErrors.billingState)} helperText={createFieldErrors.billingState} />
          <TextField margin="normal" name="billingZip" label="Billing ZIP" value={createForm.billingZip} onChange={(e) => { setCreateForm((p) => ({ ...p, billingZip: e.target.value })); setCreateFieldErrors((p) => ({ ...p, billingZip: '' })); }} fullWidth error={Boolean(createFieldErrors.billingZip)} helperText={createFieldErrors.billingZip} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate}>Cancel</Button>
          <Button variant="contained" onClick={saveCreate} disabled={saving}>{saving ? 'Saving...' : 'Create Receiver'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Receiver</DialogTitle>
        <DialogContent>
          <TextField margin="normal" name="receiverName" label="Receiver Name" value={editForm.receiverName} onChange={(e) => { setEditForm((p) => ({ ...p, receiverName: e.target.value })); setEditFieldErrors((p) => ({ ...p, receiverName: '' })); }} fullWidth error={Boolean(editFieldErrors.receiverName)} helperText={editFieldErrors.receiverName} />
          <TextField margin="normal" name="billingAddress1" label="Billing Address 1" value={editForm.billingAddress1} onChange={(e) => { setEditForm((p) => ({ ...p, billingAddress1: e.target.value })); setEditFieldErrors((p) => ({ ...p, billingAddress1: '' })); }} fullWidth error={Boolean(editFieldErrors.billingAddress1)} helperText={editFieldErrors.billingAddress1} />
          <TextField margin="normal" name="billingAddress2" label="Billing Address 2" value={editForm.billingAddress2} onChange={(e) => setEditForm((p) => ({ ...p, billingAddress2: e.target.value }))} fullWidth />
          <TextField margin="normal" name="billingCity" label="Billing City" value={editForm.billingCity} onChange={(e) => { setEditForm((p) => ({ ...p, billingCity: e.target.value })); setEditFieldErrors((p) => ({ ...p, billingCity: '' })); }} fullWidth error={Boolean(editFieldErrors.billingCity)} helperText={editFieldErrors.billingCity} />
          <TextField margin="normal" name="billingState" label="Billing State" value={editForm.billingState} onChange={(e) => { setEditForm((p) => ({ ...p, billingState: e.target.value })); setEditFieldErrors((p) => ({ ...p, billingState: '' })); }} fullWidth error={Boolean(editFieldErrors.billingState)} helperText={editFieldErrors.billingState} />
          <TextField margin="normal" name="billingZip" label="Billing ZIP" value={editForm.billingZip} onChange={(e) => { setEditForm((p) => ({ ...p, billingZip: e.target.value })); setEditFieldErrors((p) => ({ ...p, billingZip: '' })); }} fullWidth error={Boolean(editFieldErrors.billingZip)} helperText={editFieldErrors.billingZip} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Receivers;

